import {
    Button as MUIButton,
    type ButtonProps as MUIButtonProps,
    type SxProps,
    type Theme,
} from '@mui/material';
import { type ForwardRefRenderFunction, forwardRef, useMemo } from 'react';

const colors = ['inherit', 'primary', 'secondary', 'success', 'error', 'info', 'warning'] as const;
type Color = (typeof colors)[number];
const colorsSet = new Set(colors) as Set<MUIButtonProps['color']>;

const isMuiColor = (color?: string): color is MUIButtonProps['color'] =>
    colorsSet.has(color as MUIButtonProps['color']);

type ButtonProps = Omit<MUIButtonProps, 'color'> & {
    color?: MUIButtonProps['color'] | string;
};

const ButtonReffed: ForwardRefRenderFunction<HTMLButtonElement, ButtonProps> = (props, ref) => {
    const { sx: providedSX, color: providedColor, ...rest } = props;

    const color = useMemo(
        () => (isMuiColor(providedColor) ? providedColor : undefined),
        [providedColor],
    );

    const sx = useMemo(
        (): SxProps<Theme> => ({
            textTransform: 'none',
            color: colorsSet.has(providedColor as Color) ? undefined : providedColor,
            ...providedSX,
        }),
        [providedSX, providedColor],
    );

    return <MUIButton ref={ref} color={color} sx={sx} {...rest} />;
};

export const Button = forwardRef(ButtonReffed);
